import { TagMappingDto } from "@closerplatform/spinner-openapi";
import { flow, isEmpty, size } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useRef } from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { useStore } from "../../../../models";
import { usePreloader } from "../../../HOC/withPreloading/withPreloading";
import {
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../../models/useSettingsList/types";
import {
  useSettingsListEditable,
  withFilters,
  withSelectables
} from "../../models/useSettingsList/useSettingsList";
import { SettingsForm } from "../../settingsForm";
import { DeleteConfirmationToastOnError } from "../../settingsForms/deleteConfirmation/deleteConfirmation";
import { ElementFormActions } from "../../settingsForms/elementForm/actions/elementFormActions";
import { ElementForm } from "../../settingsForms/elementForm/elementForm/elementForm";
import { ElementFormCheckbox } from "../../settingsForms/elementForm/icons/elementFormCheckbox";
import { ElementFormDeleteIcon } from "../../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { ElementFormEditIcon } from "../../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormSaveDiscardIcon } from "../../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { BooleanListFilter } from "../../settingsForms/filters/booleanListFilter";
import { ListFilterWrapper } from "../../settingsForms/filters/listFilterWrapper/listFilterWrapper";
import { FilterWrapperProvider } from "../../settingsForms/filters/listFilterWrapper/useFilterWrapper";
import { MatchingStrategyFilter } from "../../settingsForms/filters/pickerFilters/matchingStrategyFilter";
import { FormPlaceholder } from "../../settingsForms/fullWidthPlaceholder/formPlaceholder";
import { VerticalSeparator } from "../../settingsForms/separators/verticalSeparator/verticalSeparator";
import { MappingBox } from "./components/mappingBox/mappingBox";
import { NewMappingBox } from "./components/newMappingBox/newMappingBox";
import { ExistingMappingFormType, NewMappingFormType } from "./types";
import {
  copyExistingMappingForm,
  createNewTagMappingForm,
  mapExistingFormToUpdateForm,
  mapNewFormToCreateForm,
  mapTagMappingToForm
} from "./utils";
import { useAgentServices } from "../../../../models/agentServices";
import {
  ListActions,
  ListDeleteAction,
  ListActionsSection,
  ListBooleanAction
} from "../../listActions";

export const TaggingSettingsList = observer(() => {
  const {
    newFormRef,
    newItem,
    handleCreateNewClick,
    discardNewItem,
    saveNewItem,

    items,
    selectedItems,
    filteredItems,
    setItemsSelected,
    setItemSelected,
    deleteAllSelected,
    forAllSelected,
    forEachSelected,
    uiRemoveExistingItem,
    apiDeleteItem,

    editedItems,
    currentlyEditedItem,
    saveEditedItemChangesLocally,
    onExistingEditClick,
    discardEditedItem,
    onSubmitAll,
    onDiscardAll
  } = useTaggingSettingsState();

  const renderNewMappingForm = (form: NewMappingFormType) => {
    const isStrategyPicked = form.$.matchingStrategy.$.length !== 0;

    return (
      <ElementForm isEdited>
        <NewMappingBox form={form} formRef={newFormRef} />
        {isStrategyPicked && (
          <>
            <VerticalSeparator />
            <ElementFormActions>
              <ElementFormSaveDiscardIcon onSave={saveNewItem} onDiscard={discardNewItem} />
            </ElementFormActions>
          </>
        )}
      </ElementForm>
    );
  };

  const renderNewMappingSection = () =>
    newItem ? (
      renderNewMappingForm(newItem)
    ) : (
      <FormPlaceholder
        onClick={handleCreateNewClick}
        text={"+ " + intl.formatMessage("settings.tagging.addNewTagging")}
      />
    );

  const renderElementForm = (item: ExistingMappingFormType, onDeleteClick: () => void) => {
    const isCurrentlyEdited = !!currentlyEditedItem && item.$.id.$ === currentlyEditedItem.$.id.$;
    const currentForm = isCurrentlyEdited && currentlyEditedItem ? currentlyEditedItem : item;
    const { id } = currentForm.$;

    return (
      <ElementForm isEdited={isCurrentlyEdited}>
        {!isCurrentlyEdited && (
          <>
            <ElementFormActions>
              <ElementFormCheckbox
                id={id.$}
                isSelected={!!selectedItems[item.$.id.$]}
                onChange={(val) => setItemSelected(item, val)}
              />
            </ElementFormActions>
            <VerticalSeparator />
          </>
        )}
        <MappingBox form={currentForm} isEdited={isCurrentlyEdited} />
        <VerticalSeparator />
        {isCurrentlyEdited ? (
          <ElementFormActions>
            <ElementFormSaveDiscardIcon
              onSave={saveEditedItemChangesLocally}
              onDiscard={discardEditedItem}
            />
          </ElementFormActions>
        ) : (
          <ElementFormActions>
            <ElementFormEditIcon onClick={() => onExistingEditClick(item)} />
            <ElementFormDeleteIcon onClick={onDeleteClick} />
          </ElementFormActions>
        )}
      </ElementForm>
    );
  };

  const renderExistingItems = () => (
    <div>
      {filteredItems.map((item) => {
        return (
          <DeleteConfirmationToastOnError
            key={item.$.id.$}
            areYouSureText={intl.formatMessage("settings.tagging.areYouSureToRemove")}
            succesfullyDeletedText={intl.formatMessage("settings.tagging.deleteConfirmation")}
            onDelete={() => apiDeleteItem(item.$.id.$)}
            onTimeoutPassed={() => uiRemoveExistingItem(item.$.id.$)}
            renderComponent={(onDeleteClick) => renderElementForm(item, onDeleteClick)}
          />
        );
      })}
    </div>
  );

  const renderFilterSection = () => {
    return (
      <FilterWrapperProvider<ExistingMappingFormType>>
        <ListFilterWrapper<ExistingMappingFormType>
          placeholder={intl.formatMessage("settings.tags.filters.placeholder")}
          allCount={items.length}
          filteredCount={filteredItems.length}
          inputPredicateId="TagInputFilter"
          inputPredicate={(item, input) => caseInsensitiveIncludes(item.$.tag.$.tag.$, input)}
          headerText={intl.formatMessage("settings.tagging.filters.header")}
        >
          <BooleanListFilter<ExistingMappingFormType>
            description={intl.formatMessage("settings.tags.filters.active")}
            filterId="MappingActive"
            predicate={(item) => item.$.enabled.$}
          />
          <MatchingStrategyFilter />
        </ListFilterWrapper>
      </FilterWrapperProvider>
    );
  };

  const renderActionSection = () => {
    const areAllSelected = !!filteredItems.length && size(selectedItems) === size(filteredItems);

    return (
      <SettingsForm
        disabled={isEmpty(editedItems)}
        onDiscard={onDiscardAll}
        onSubmit={onSubmitAll}
        displayInline
      >
        <ListActions
          isChecked={areAllSelected}
          onCheckboxChanged={(val) => setItemsSelected(filteredItems, val)}
          isShowingActions={!isEmpty(selectedItems)}
        >
          <VerticalSeparator style="margin-halfLeft" />
          <ListActionsSection>
            <ListBooleanAction
              text={intl.formatMessage("settings.tagging.mapping.enabled")}
              isActive={forAllSelected((item) => item.$.enabled.$)}
              onClick={(val) => forEachSelected((i) => i.$.enabled.reset(val))}
            />
          </ListActionsSection>
          <VerticalSeparator />
          <ListDeleteAction onDelete={deleteAllSelected} />
        </ListActions>
      </SettingsForm>
    );
  };

  return (
    <>
      {renderNewMappingSection()}
      {renderFilterSection()}
      {!isEmpty(filteredItems) && renderActionSection()}
      {renderExistingItems()}
    </>
  );
});

type HookParams = Params<ExistingMappingFormType, NewMappingFormType, TagMappingDto>;
type HookEditable = ListHookEditable<ExistingMappingFormType, NewMappingFormType>;
type HookSelectable = ListHookWithSelectables<
  ExistingMappingFormType,
  NewMappingFormType,
  HookEditable
>;
type HookFilterable = ListHookWithFilters<
  ExistingMappingFormType,
  NewMappingFormType,
  HookSelectable
>;

const useListHook = flow<[HookParams], HookEditable, HookSelectable, HookFilterable>(
  useSettingsListEditable,
  withSelectables,
  withFilters
);

const useTaggingSettingsState = () => {
  const { tagsStore } = useStore();
  const { setLoadingState } = usePreloader();
  const { spinnerService } = useAgentServices();
  const newFormRef = useRef<HTMLDivElement | null | undefined>();

  const resetSelected = () => {
    listHook.deselectAll();
  };

  const listHook = useListHook({
    apiGetItems: () => spinnerService.getTagMappings(),
    apiCreateItem: (item) => spinnerService.createTagMapping(mapNewFormToCreateForm(item)),
    apiUpdateItem: (item) =>
      spinnerService.updateTagMapping(item.$.id.$, mapExistingFormToUpdateForm(item)),
    apiDeleteItem: (id) => spinnerService.deleteTagMapping(id),
    mapItemToForm: mapTagMappingToForm,
    createNewForm: createNewTagMappingForm,

    copyExistingForm: copyExistingMappingForm,

    saveErrorId: "settings.tagging.error.wrongMapping",
    onSubmitCallback: resetSelected,
    onDiscardCallback: resetSelected,
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    tagsStore.loadTags();
  }, []);

  useEffect(() => {
    listHook.items.forEach((item) => {
      item.$.enabled.onUpdate(() => listHook.setItemEdited(item));
    });
  }, [listHook.items]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (
        listHook.newItem &&
        listHook.newItem.$.matchingStrategy.value.length === 0 &&
        newFormRef.current &&
        event.target &&
        !newFormRef.current.contains(event.target as Node)
      ) {
        listHook.discardNewItem();
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [listHook.newItem]);

  return {
    ...listHook,
    newFormRef
  };
};
